import type { ComponentProps } from 'react'
import { CashDrawerSetupNotice, MoneyOverview } from '@/components/money/MoneyOverview'
import {
  canOperateCashDrawers as canOperateDrawer,
  canViewStatements,
} from '@/lib/services/money/money.access'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { StyledIcon } from '@/components/ui/styled-icon'
import { Typography } from '@/components/ui/typography'
import { useOrganizationContext } from '@/hooks/useOrganizationContext'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useAbility } from '@/lib/casl'
import { adminTitleHead } from '@/lib/admin-route-title'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Banknote, Send, UsersRound, Wallet } from 'lucide-react'

type MoneyPageAction = ComponentProps<typeof MoneyOverview>['actions'][number] & { action: string }

export const Route = createFileRoute('/_authenticated/$orgId/money/')({
  component: MoneyPage,
  head: () => adminTitleHead('Money'),
})
function MoneyPage() {
  const { orgId } = Route.useParams()
  const path = usePathHelper()
  const ability = useAbility()
  const canReport = ability.can('list', 'transactions')
  const canStatements = canViewStatements(ability)
  const { organization } = useOrganizationContext()
  const canOperateCashDrawers = canOperateDrawer(ability, { organization })
  const canViewCashDrawerReports = ability.can('list', 'cash-drawer-reports')
  const actions: MoneyPageAction[] = [
    ...(canOperateCashDrawers
      ? [
          {
            key: 'cashDrawerBalancing',
            title: 'Start or count a cash drawer',
            description:
              'Start the cash drawer, record cash movements, and count it at the end of the day.',
            icon: Banknote,
            tone: 'mint',
            action: 'Manage cash drawer',
          } satisfies MoneyPageAction,
        ]
      : []),
    ...(canReport
      ? [
          {
            key: 'customerBalances',
            title: 'Manage customer balances',
            description:
              'See what customers owe, what’s past due, and how long it’s been outstanding.',
            icon: UsersRound,
            tone: 'mint',
            action: 'View balances',
          } satisfies MoneyPageAction,
        ]
      : []),
    ...(canStatements
      ? [
          {
            key: 'statements',
            title: 'Send customer statements',
            description: 'Review open invoices, then email or print statements in one batch.',
            icon: Send,
            tone: 'amethyst',
            action: 'Prepare statements',
          } satisfies MoneyPageAction,
        ]
      : []),
  ]
  return (
    <div className="@container/money space-y-6 pb-6">
      <AdminPageHeader
        title="Money"
        icon={Wallet}
        description="Manage cash drawers, customer balances, statements, and financial reports."
      />
      <CashDrawerSetupNotice orgId={orgId} organization={organization} ability={ability} />
      {canReport ? (
        <MoneyOverview orgId={orgId} actions={actions} />
      ) : (
        <div className="@container">
          <div className="grid gap-4 @3xl:grid-cols-3">
            {actions.map((item) => (
              <Card key={item.key} className="flex flex-col">
                <CardHeader>
                  <StyledIcon icon={item.icon} tone={item.tone} />
                  <CardTitle>{item.title}</CardTitle>
                  <CardDescription>{item.description}</CardDescription>
                </CardHeader>
                <CardContent className="mt-auto">
                  <Button asChild variant="outline">
                    <Link {...path[item.key].index().using({ orgId }).link}>{item.action} →</Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
      {canReport || ability.can('view', 'payouts') || canViewCashDrawerReports ? (
        <section className="space-y-3" aria-label="Related reports">
          <Typography variant="sectionTitle">Related reports</Typography>
          <div className="flex flex-wrap gap-3">
            {canReport ? (
              <>
                <Button variant="outline" asChild>
                  <Link {...path.salesSummary.index().using({ orgId }).link}>Sales Summary</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link {...path.salesStatistics.index().using({ orgId }).link}>
                    Sales Statistics
                  </Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link {...path.externalOrders.index().using({ orgId }).link}>
                    Incoming Wire Orders
                  </Link>
                </Button>
              </>
            ) : null}
            {ability.can('view', 'payouts') ? (
              <Button variant="outline" asChild>
                <Link {...path.deposits.index().using({ orgId }).link}>Bank Deposits</Link>
              </Button>
            ) : null}
            {canViewCashDrawerReports ? (
              <Button variant="outline" asChild>
                <Link {...path.cashDrawer.index().using({ orgId }).link}>Cash Drawer Report</Link>
              </Button>
            ) : null}
            <Button variant="link" asChild>
              <Link {...path.reports.index().using({ orgId }).link}>Browse all reports →</Link>
            </Button>
          </div>
        </section>
      ) : null}
    </div>
  )
}
